/**
 * Embedded host bridge probe — single source of truth for "is the AAR's native
 * media bridge available?".
 *
 * The host (AAR) installs two things, at DIFFERENT moments:
 *   1. `window.__effluxEmbedded` — injected in `onPageStarted`, i.e. before the
 *      bundle runs. Knowing we are embedded is therefore immediate and reliable.
 *   2. `window.EffluxHost` — `addJavascriptInterface`, whose object may not be
 *      visible to the very first scripts of the document.
 *
 * That gap is a real boot race: a consumer that probes `EffluxHost` too early
 * sees nothing and, if it memoizes that answer, stays wrong for the whole
 * session. `EmbeddedMediaCache` already guarded against it with a poll; this
 * module hosts that logic so `cacheFactory` uses the exact same rules.
 *
 * Key distinction — "not injected YET" vs "will never have the bridge":
 *   - `EffluxHost` ABSENT → the interface has not landed yet → wait.
 *   - `EffluxHost` PRESENT but without the media methods → old AAR (the bridge
 *     is injected atomically with all its @JavascriptInterface methods, so a
 *     partial object is a permanent property of the installed host) → decide
 *     immediately, no waiting, and let the caller fall back to the browser
 *     cache. Back-compat costs zero extra latency.
 */

/** Default budget for the boot-path decision (the bridge lands in ms). */
export const HOST_BRIDGE_WAIT_MS = 3_000;
/** Budget for the first native download after boot (generous, one-off). */
export const HOST_BRIDGE_DOWNLOAD_WAIT_MS = 10_000;
const POLL_MS = 200;

/** Native media API exposed by the AAR (`addJavascriptInterface` "EffluxHost"). */
export interface EffluxHostMediaBridge {
  cacheMedia(id: string, url: string, expectedSize: string): void;
  isMediaCached(id: string): boolean;
  mediaSize(id: string): string;
  mediaCacheUsage(): string;
  removeMedia(id: string): void;
  cancelMedia(id: string): void;
  clearMediaCache(): void;
}

interface EmbeddedWindow {
  __effluxEmbedded?: unknown;
  EffluxHost?: Partial<EffluxHostMediaBridge>;
}

function embeddedWindow(): EmbeddedWindow | null {
  return typeof window === 'undefined'
    ? null
    : (window as unknown as EmbeddedWindow);
}

/**
 * True inside the host WebView (AAR). Known from the first line of the bundle —
 * unlike `EffluxHost`, this marker never races.
 */
export function isEmbeddedPlatform(): boolean {
  return !!embeddedWindow()?.__effluxEmbedded;
}

/** The native media bridge, or null when absent/incomplete. */
export function getNativeMediaBridge(): EffluxHostMediaBridge | null {
  const host = embeddedWindow()?.EffluxHost;
  if (
    !host ||
    typeof host.cacheMedia !== 'function' ||
    typeof host.isMediaCached !== 'function'
  ) {
    return null;
  }
  return host as EffluxHostMediaBridge;
}

/** True when the host already exposes the native media API. */
export function hasNativeMediaBridge(): boolean {
  return getNativeMediaBridge() !== null;
}

/**
 * True when `EffluxHost` exists but lacks the media methods — an old AAR, which
 * will never gain them. Lets callers stop waiting and fall back right away.
 */
function isLegacyHost(): boolean {
  const host = embeddedWindow()?.EffluxHost;
  return !!host && typeof host.cacheMedia !== 'function';
}

/**
 * Waits for the native media bridge, bounded by `timeoutMs`.
 *
 * Returns as soon as the answer is conclusive: the bridge appeared, or
 * `EffluxHost` is present without it (old AAR). Only a host that never injects
 * `EffluxHost` at all pays the full timeout.
 */
export async function waitForNativeMediaBridge(
  timeoutMs: number = HOST_BRIDGE_WAIT_MS,
): Promise<EffluxHostMediaBridge | null> {
  const immediate = getNativeMediaBridge();
  if (immediate) return immediate;
  if (isLegacyHost()) return null;

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    const bridge = getNativeMediaBridge();
    if (bridge) return bridge;
    if (isLegacyHost()) return null;
  }
  return null;
}
